/**
 * Geschäftspartner-Verwaltung: Ein Partner kann gleichzeitig Kunde, Interessent
 * und Lieferant sein. Die Rollen-Flags entscheiden, welche Fachattribute gelten.
 */
/** Filterwerte der Partnerliste; `all` bedeutet „ohne Rollenfilter“. */
export const PARTNER_TYPES = ['all', 'customer', 'prospect', 'supplier'];
/** Pipeline-Stufen des Vertriebstrichters in fachlicher Reihenfolge. */
export const LEAD_STATUSES = [
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'PROPOSAL_SENT',
    'NEGOTIATION',
    'WON',
    'LOST',
];
/** Stufen, die ein Lead im Trichter noch durchläuft; `WON`/`LOST` sind Endzustände. */
export const OPEN_LEAD_STATUSES = [
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'PROPOSAL_SENT',
    'NEGOTIATION',
];
export function isClosedLeadStatus(status) {
    return status === 'WON' || status === 'LOST';
}
/** Standard-Abschlusswahrscheinlichkeit je Stufe (%), solange nichts Eigenes gepflegt ist. */
export const LEAD_STATUS_DEFAULT_PROBABILITY = {
    NEW: 10,
    CONTACTED: 20,
    QUALIFIED: 40,
    PROPOSAL_SENT: 60,
    NEGOTIATION: 80,
    WON: 100,
    LOST: 0,
};
export const LEAD_SOURCES = [
    'WEBSITE',
    'TRADE_FAIR',
    'REFERRAL',
    'COLD_OUTREACH',
    'INBOUND_CALL',
    'OTHER',
];
export const LEAD_SCORE_MIN = 0;
export const LEAD_SCORE_MAX = 100;
/** Lieferantenbewertung: A zuverlässig, B akzeptabel, C kritisch. */
export const SUPPLIER_RATINGS = ['A', 'B', 'C'];
// ---------------------------------------------------------------------------
// Risikomanagement: Insolvenz, Inkasso und Verkaufssperre
// ---------------------------------------------------------------------------
/** Risikoeinstufung eines Geschäftspartners in aufsteigender Eskalation. */
export const PARTNER_RISK_STATUSES = [
    'NORMAL',
    'WATCHLIST',
    'DELIVERY_LOCK',
    'SALES_LOCK',
    'COLLECTION',
    'INSOLVENCY',
];
/** Einstufungen, die jede Belegerstellung im Verkauf unterbinden. */
export const SALES_BLOCKING_RISK_STATUSES = [
    'SALES_LOCK',
    'COLLECTION',
    'INSOLVENCY',
];
/** Einstufungen, die zwingend zur automatischen Sperre führen (§ Inkasso/Insolvenz). */
export const AUTO_BLOCKING_RISK_STATUSES = [
    'COLLECTION',
    'INSOLVENCY',
];
export function isAutoBlockingRiskStatus(status) {
    return AUTO_BLOCKING_RISK_STATUSES.includes(status);
}
/** Einstufungen, die über den Sperr-Endpunkt gesetzt werden dürfen. */
export const PARTNER_LOCK_RISK_STATUSES = [
    'DELIVERY_LOCK',
    'SALES_LOCK',
    'COLLECTION',
    'INSOLVENCY',
];
/** Status der Warenkreditversicherung (WKV) vom Deckungsantrag bis zur Entschädigung. */
export const CREDIT_INSURANCE_STATUSES = [
    'NOT_INSURED',
    'INSURED',
    'LIMIT_EXCEEDED',
    'CLAIM_REPORTED',
    'INDEMNIFIED',
];
export const PARTNER_BLOCK_REASON_MAX_LENGTH = 500;
/**
 * Verkauf und Lieferung sind gesperrt, sobald die Sperre gesetzt ist oder die
 * Risikoeinstufung eine Verkaufssperre auslöst. Web und API werten dieselbe Regel aus.
 */
export function isPartnerSalesBlocked(partner) {
    return partner.isBlocked || SALES_BLOCKING_RISK_STATUSES.includes(partner.riskStatus);
}
/** Incoterms 2020, beschränkt auf die im Handel gebräuchlichen Klauseln. */
export const INCOTERMS = [
    'EXW',
    'FCA',
    'FAS',
    'FOB',
    'CFR',
    'CIF',
    'CPT',
    'CIP',
    'DAP',
    'DPU',
    'DDP',
];
/** Präfixe der Partnernummern je Rolle (Nummernkreise CUSTOMER/PROSPECT/SUPPLIER). */
export const PARTNER_NUMBER_PREFIXES = {
    customer: 'KD-',
    prospect: 'INT-',
    supplier: 'LF-',
};
/** SKR03/SKR04-Konvention: Debitoren 10000–69999, Kreditoren 70000–99999. */
export const DEBTOR_ACCOUNT_MIN = 10_000;
export const DEBTOR_ACCOUNT_MAX = 69_999;
export const CREDITOR_ACCOUNT_MIN = 70_000;
export const CREDITOR_ACCOUNT_MAX = 99_999;
export function isDebtorAccountInRange(account) {
    const value = Number(account);
    return Number.isInteger(value) && value >= DEBTOR_ACCOUNT_MIN && value <= DEBTOR_ACCOUNT_MAX;
}
export function isCreditorAccountInRange(account) {
    const value = Number(account);
    return Number.isInteger(value) && value >= CREDITOR_ACCOUNT_MIN && value <= CREDITOR_ACCOUNT_MAX;
}
/** Zulässige Sortierspalten der Partnerliste — sie fließen direkt in die Query. */
export const PARTNER_SORT_FIELDS = [
    'partnerNumber',
    'searchTerm',
    'city',
    'status',
    'industry',
    'industryCode',
    'leadStatus',
    'leadScore',
    'supplierRating',
    'riskStatus',
    'createdAt',
    'updatedAt',
];
export const SUPPLIER_PRODUCT_SORT_FIELDS = [
    'supplierSku',
    'purchasePrice',
    'leadTimeDays',
    'createdAt',
];
/**
 * Rollenbindung dynamischer Zusatzfelder: Die Gruppe eines Feldes darf mit
 * `customer:`, `prospect:` oder `supplier:` beginnen. Nur Partner mit der
 * jeweiligen Rolle sehen das Feld; ohne Präfix gilt es für alle Rollen.
 */
export const PARTNER_FIELD_SCOPE_PREFIXES = ['customer', 'prospect', 'supplier'];
export function resolvePartnerFieldScope(group) {
    if (group === null) {
        return { scope: null, label: '' };
    }
    const separator = group.indexOf(':');
    if (separator === -1) {
        return { scope: null, label: group };
    }
    const candidate = group.slice(0, separator).trim().toLowerCase();
    const scope = PARTNER_FIELD_SCOPE_PREFIXES.find((prefix) => prefix === candidate);
    return scope === undefined
        ? { scope: null, label: group }
        : { scope, label: group.slice(separator + 1).trim() };
}
export function isPartnerFieldVisible(group, roles) {
    const { scope } = resolvePartnerFieldScope(group);
    if (scope === null) {
        return true;
    }
    if (scope === 'customer') {
        return roles.isCustomer;
    }
    if (scope === 'prospect') {
        return roles.isProspect;
    }
    return roles.isSupplier;
}
//# sourceMappingURL=partners.js.map